import Translations from './pages/Translations';
import { SettingsPageAccess } from './components/shared/SettingsPageAccess';
import React, { Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { Loading } from '@repo/ui';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { CompanyProvider } from './context/CompanyContext';
import { TranslationProvider } from './context/TranslationContext';
import axiosInstance from './api/axios';
import Layout from './components/Layout';
import { AppsProvider } from './context/AppsContext';
import { UiPreferencesGate } from './components/UiPreferencesGate';
import { moduleRegistry } from './modules/ModuleRegistry';

import Login from './pages/Login';
import RedirectToPlatformApps from './components/RedirectToPlatformApps';

moduleRegistry.registerLoader('finance', () => import('./modules/finance/index'));
moduleRegistry.registerLoader('inventory', () => import('./modules/inventory/index'));
moduleRegistry.registerLoader('accounting', () => import('./modules/accounting/index'));
moduleRegistry.registerLoader('sales', () => import('./modules/sales/index'));
moduleRegistry.registerLoader('pos', () => import('./modules/pos/index'));
moduleRegistry.registerLoader('procurement', () => import('./modules/procurement/index'));
moduleRegistry.registerLoader('demand', () => import('./modules/demand/index'));
moduleRegistry.registerLoader('manufacturing', () => import('./modules/manufacturing/index'));
moduleRegistry.registerLoader('assets', () => import('./modules/assets/index'));
moduleRegistry.registerLoader('import', () => import('./modules/import/index'));
moduleRegistry.registerLoader('export', () => import('./modules/export/index'));
moduleRegistry.registerLoader('budget', () => import('./modules/budget/index'));
moduleRegistry.registerLoader('approval', () => import('./modules/approval/index'));
moduleRegistry.registerLoader('consent', () => import('./modules/consent/index'));
moduleRegistry.registerLoader('settings', () => import('./modules/settings/index'));

const ComingSoon = React.lazy(() => import('./pages/ComingSoon'));
const HeaderSearchPage = React.lazy(() => import('./pages/HeaderSearchPage'));
const HeaderNotificationsPage = React.lazy(() => import('./pages/HeaderNotificationsPage'));
const CustomFields = React.lazy(() => import('./pages/CustomFields'));
function ErpHeaderSearchPage() {
  const modules = React.useMemo(() => [], []);
  return <HeaderSearchPage modules={modules} httpClient={axiosInstance} />;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isInitialized } = useAuth();

  if (!isInitialized) {
    return <Loading fullScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/** Modül ana path'ine (/inventory gibi) girildiğinde dashboard sayfasına yönlendirir. */
function RedirectToModuleDashboard({ moduleId }: { moduleId: string }) {
  const menuItems = moduleRegistry.getModuleMenuItems(moduleId);
  const dashboardPath = menuItems.find((m) => m.id === 'dashboard')?.path ?? `/${moduleId}/dashboard`;
  return <Navigate to={dashboardPath} replace />;
}

function RedirectLegacyErpModulePath() {
  const { moduleId = '', '*': rest = '' } = useParams<{ moduleId: string; '*': string }>();
  const normalizedModuleId = moduleId.trim().toLowerCase();
  const normalizedRest = rest.replace(/^\/+/, '');
  const targetPath = normalizedRest
    ? `/${normalizedModuleId}/${normalizedRest}`
    : `/${normalizedModuleId}`;
  return <Navigate to={targetPath} replace />;
}

function getModuleRouteRelativePath(moduleId: string, routePath: string): string | null {
  const normalizedRoute = routePath.replace(/\/+$/, '') || '/';
  const canonicalPrefix = `/${moduleId}`;
  const legacyErpPrefix = `/erp/${moduleId}`;

  for (const prefix of [canonicalPrefix, legacyErpPrefix]) {
    if (normalizedRoute === prefix || normalizedRoute.startsWith(`${prefix}/`)) {
      return normalizedRoute.slice(prefix.length).replace(/^\//, '');
    }
  }

  return null;
}

function renderModuleChildRoutes(
  moduleId: string,
  routes: Array<{ path: string; component: any; exact?: boolean }>,
  keyPrefix: string,
) {
  return routes.map((route, index) => {
    const relativePath = getModuleRouteRelativePath(moduleId, route.path);
    if (relativePath === null) return null;

    const Component = route.component;
    const routeMatch = relativePath ? { path: relativePath } : { index: true as const };

    return (
      <Route
        key={`${keyPrefix}-${index}`}
        {...routeMatch}
        element={
          <Suspense fallback={<Loading />}>
            <Component />
          </Suspense>
        }
      />
    );
  });
}

function AppRoutes() {
  const [modulesLoaded, setModulesLoaded] = useState(false);
  const [moduleRoutesMap, setModuleRoutesMap] = useState<Map<string, Array<{ path: string; component: any; exact?: boolean }>>>(new Map());
  const navigate = useNavigate();

  useEffect(() => {
    const handleLogout = (event: CustomEvent) => {
      navigate(event.detail.path, { replace: true });
    };

    window.addEventListener('qubepro-logout', handleLogout as EventListener);
    return () => {
      window.removeEventListener('qubepro-logout', handleLogout as EventListener);
    };
  }, [navigate]);

  useEffect(() => {
    async function loadModules() {
      try {
        await moduleRegistry.loadAllModules();
        const routes = moduleRegistry.getAllRoutes();

        const routesByModule = new Map<string, Array<{ path: string; component: any; exact?: boolean }>>();
        routes.forEach(({ moduleId, route }) => {
          if (!routesByModule.has(moduleId)) {
            routesByModule.set(moduleId, []);
          }
          routesByModule.get(moduleId)!.push(route);
        });

        setModuleRoutesMap(routesByModule);
        setModulesLoaded(true);
      } catch (error) {
        console.error('Failed to load modules:', error);
        setModulesLoaded(true);
      }
    }

    loadModules();
  }, []);

  return (
    <Suspense fallback={<Loading fullScreen />}>
      <CompanyProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<Navigate to="/inventory/dashboard" replace />} />
          <Route path="/qubepro" element={<RedirectToPlatformApps />} />
          <Route path="/apps" element={<RedirectToPlatformApps />} />
          <Route path="/erp" element={<Navigate to="/inventory/dashboard" replace />} />
          <Route path="/erp/" element={<Navigate to="/inventory/dashboard" replace />} />
          <Route path="/erp/:moduleId" element={<RedirectLegacyErpModulePath />} />
          <Route path="/erp/:moduleId/*" element={<RedirectLegacyErpModulePath />} />
          {[
            'qubepro-ai', 'support', 'knowledge', 'signature', 'studio', 'rental',
            'chat', 'documents', 'project', 'timesheet', 'field', 'planning',
            'helpdesk', 'website', 'email', 'purchase',
            'admin'
          ].map(path => (
            <Route
              key={path}
              path={`/${path}`}
              element={
                <ProtectedRoute>
                  <ComingSoon />
                </ProtectedRoute>
              }
            />
          ))}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/inventory/dashboard" replace />} />
          </Route>
          <Route
            path="/erp/finance/*"
            element={
              <ProtectedRoute>
                {modulesLoaded ? <Layout /> : <Loading fullScreen />}
              </ProtectedRoute>
            }
          >
            {modulesLoaded && (
              <>
                <Route index element={<Navigate to="/erp/finance/dashboard" replace />} />
                {renderModuleChildRoutes('finance', moduleRoutesMap.get('finance') ?? [], 'erp-finance')}
              </>
            )}
          </Route>
          <Route
            path="/erp/sales/*"
            element={
              <ProtectedRoute>
                {modulesLoaded ? <Layout /> : <Loading fullScreen />}
              </ProtectedRoute>
            }
          >
            {modulesLoaded && (
              <>
                <Route index element={<Navigate to="/erp/sales/dashboard" replace />} />
                {renderModuleChildRoutes('sales', moduleRoutesMap.get('sales') ?? [], 'erp-sales')}
              </>
            )}
          </Route>
          <Route
            path="/erp/procurement/*"
            element={
              <ProtectedRoute>
                {modulesLoaded ? <Layout /> : <Loading fullScreen />}
              </ProtectedRoute>
            }
          >
            {modulesLoaded && (
              <>
                <Route index element={<Navigate to="/erp/procurement/dashboard" replace />} />
                {renderModuleChildRoutes('procurement', moduleRoutesMap.get('procurement') ?? [], 'erp-procurement')}
              </>
            )}
          </Route>
                    <Route path="/translations" element={<ProtectedRoute><Layout /></ProtectedRoute>}><Route index element={<SettingsPageAccess permission="translation:read"><Translations /></SettingsPageAccess>} /></Route>
<Route path="/custom-fields"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route
              index
              element={
                <Suspense fallback={<Loading />}>
                  <SettingsPageAccess permission="customField:read"><CustomFields /></SettingsPageAccess>
                </Suspense>
              }
            />
          </Route>
          {moduleRegistry.getRegisteredModuleIds().map((moduleId) => {
            const routes = moduleRoutesMap.get(moduleId) ?? [];
            return (
              <Route
                key={`module-${moduleId}`}
                path={`/${moduleId}/*`}
                element={
                  <ProtectedRoute>
                    {modulesLoaded ? <Layout /> : <Loading fullScreen />}
                  </ProtectedRoute>
                }
              >
                {modulesLoaded && (
                  <>
                    {!routes.some(route => getModuleRouteRelativePath(moduleId, route.path) === '') && (
                      <Route index element={<RedirectToModuleDashboard moduleId={moduleId} />} />
                    )}
                    {renderModuleChildRoutes(moduleId, routes, moduleId)}
                    <Route
                      path="search"
                      element={
                        <Suspense fallback={<Loading />}>
                          <ErpHeaderSearchPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="notifications"
                      element={
                        <Suspense fallback={<Loading />}>
                          <HeaderNotificationsPage />
                        </Suspense>
                      }
                    />
                  </>
                )}
              </Route>
            );
          })}
        </Routes>
      </CompanyProvider>
    </Suspense>
  );
}

const BASE_PATH = (import.meta.env.VITE_BASE_PATH || '').replace(/\/+$/, '');

function App() {
  useEffect(() => {
    const el = document.getElementById('initial-loading');
    if (el) {
      el.style.transition = 'opacity 0.2s ease';
      el.style.opacity = '0';
      setTimeout(() => el.remove(), 200);
    }
  }, []);

  return (
    <BrowserRouter basename={BASE_PATH}>
      <ThemeProvider>
        <AuthProvider>
          <AppsProvider>
            <TranslationProvider>
              <UiPreferencesGate>
                <AppRoutes />
              </UiPreferencesGate>
            </TranslationProvider>
          </AppsProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
